import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { CommunityService } from '@common/services/community.service';
import { AuthService } from '@common/services/auth.service';
import { InMemoryQueryService } from '@common/services/inmemory.query.service';
import { ContentService } from '@common/services/content.service';
import { LanguageService } from '@common/services/language.service';
import { CmsInfoItemsProvider } from '@common/components/info/cms-info-items-provider';
import { MenuItem, MenuService } from '@enus/services/menu.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    standalone: false
})
export class MenuComponent implements OnInit {
    @ViewChildren(NgbDropdown) dropdowns!: QueryList<NgbDropdown>;

    showMenu = true;
    showMenuLinks = true;
    regex = /\/|\?|\#/;
    navbarCollapsed = true;
    items: MenuItem[] = [];

    public get citySelected(): boolean {
        return this.communityCode !== 'texas';
    }

    get communityIsValid() {
        return this.communityService.communityIsValid;
    }
    get communityCode() {
        return this.communityService.communityCode;
    }

    get page() {
        return window.location.pathname.split(this.regex)[3];
    }
    get languageCode() {
        return this.languageService.currentLanguageCode;
    }

    get languages() {
        return this.languageService.availableLocales;
    }

    get targetLanguageCode() {
        return this.languages
            .filter((language) => language !== this.languageCode)
            .toString();
    }

    get urlPrefix() {
        return `/${this.communityCode}/${this.languageCode}`;
    }

    get showRegisterButton() {
        return !this.router.url.startsWith(`${this.urlPrefix}/register`);
    }

    private phase$: Observable<string>;
    actualPhase: string;

    constructor(
        public communityService: CommunityService,
        public authService: AuthService,
        private queryService: InMemoryQueryService,
        private languageService: LanguageService,
        private cmsInfoItemsProvider: CmsInfoItemsProvider,
        public contentService: ContentService,
        private menuService: MenuService,
        private router: Router,
    ) {}

    moreInfoDropdownClick() {
        this.navbarCollapsed = true;
        this.dropdowns.forEach((dropdown) => dropdown.close());
    }

    ngOnInit(): void {
        this.queryService.getObservable$('app-menu').subscribe((e) => {
            if (e.name === 'showMenu' && e.value !== null) {
                this.showMenu = e.value;
            } else if (e.name === 'showMenuLinks' && e.value !== null) {
                this.showMenuLinks = e.value;
            }
        });

        this.phase$ = combineLatest([
            this.communityService.currentAuctionOpenForNewRegistrations$,
            this.communityService.currentAuctionOpenForDecisions$,
        ]).pipe(
            map(([openForNewRegistrations, openForDecisions]) => {
                if (openForDecisions) {
                    return 'acceptation';
                }
                if (openForNewRegistrations) {
                    return 'registration';
                }
                return 'ii';
            }),
        );

        combineLatest([
            this.phase$,
            this.menuService.shouldShowWebinars$,
            this.menuService.getInfoItemSpecificUrlSegment('who-is-ichoosr'),
            this.menuService.getInfoItemSpecificUrlSegment('why-should-i-switch'),
            this.menuService.getInfoItemSpecificUrlSegment('webinars'),
            this.menuService.getInfoItemSpecificUrlSegment('press-release'),
            this.menuService.getInfoItemSpecificUrlSegment('newsletter'),
        ]).subscribe(
            ([
                phase,
                shouldShowWebinars,
                whoIsIchoosrUrlSegment,
                whyShouldISwitchUrlSegment,
                webinarsUrlSegment,
                pressReleaseUrlSegment,
                newsletterUrlSegment,
            ]) => {
                const infoTabMenuItems = this.cmsInfoItemsProvider
                    .GetItems(phase as string)
                    .map((item) => {
                        const menuItem: MenuItem = {
                            cmsKey: 'info-item-' + item.id + '-header',
                            id: item.id,
                            url:
                                this.urlPrefix +
                                '/info/' +
                                (item.url ? item.url : item.id),
                        };
                        return menuItem;
                    });

                this.items = [
                    {
                        cmsKey: 'main-menu-info',
                        id: 'main-menu-info',
                        children: infoTabMenuItems,
                    },
                    {
                        cmsKey: 'info-item-why-should-i-switch-header',
                        id: 'why-should-i-switch',
                        url: `${this.urlPrefix}/info/${whyShouldISwitchUrlSegment}`,
                    },
                    {
                        cmsKey: 'main-menu-about-us',
                        id: 'main-menu-about-us',
                        children: [
                            {
                                cmsKey: 'info-item-press-release',
                                id: 'press-release',
                                url: `${this.urlPrefix}/info/${pressReleaseUrlSegment}`,
                            },
                            {
                                cmsKey: 'info-item-who-is-ichoosr-header',
                                id: 'who-is-ichoosr',
                                url: `${this.urlPrefix}/info/${whoIsIchoosrUrlSegment}`,
                            },
                            {
                                cmsKey: 'info-item-newsletter-header',
                                id: 'newsletter',
                                url: `${this.urlPrefix}/info/${newsletterUrlSegment}`,
                            }
                        ],
                    },
                    {
                        cmsKey: 'main-menu-questions',
                        id: 'main-menu-questions',
                        children: [
                            {
                                cmsKey: 'menu-item-faq',
                                id: 'faq',
                                url: `${this.urlPrefix}/faq`,
                            },
                            {
                                cmsKey: 'menu-item-contact',
                                id: 'contact',
                                url: `${this.urlPrefix}/contact`,
                            },
                        ],
                    },
                    {
                        cmsKey: 'main-menu-community-leaders',
                        id: 'main-menu-community-leaders',
                        children: [
                            {
                                cmsKey: 'info-item-partner-cities',
                                id: 'info-item-partner-cities',
                                url: `${this.urlPrefix}/info/partner-cities`,
                            },
                            {
                                cmsKey: 'info-item-community-leader',
                                id: 'info-item-community-leader',
                                url: `${this.urlPrefix}/info/community-leader`,
                            },
                        ],
                    },
                ];

                if (shouldShowWebinars) {
                    const webinarsItem = {
                        cmsKey: 'info-item-webinars-header',
                        id: 'webinars',
                        url: `${this.urlPrefix}/info/${webinarsUrlSegment}`,
                    };
                    const webinarsParentItem = this.items.find(
                        (item) => item.id === 'main-menu-questions',
                    );
                    webinarsParentItem.children.push(webinarsItem);
                }
            },
        );

        this.menuService.menuItems = this.items;
    }

    public setLanguage(language: string): void {
        this.languageService.setLanguage(language, this.communityCode);
    }
}
